import { BookOpenIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  hitLabel,
  type SearchUserGuideArgs,
  type SearchUserGuideResult,
} from '@/features/agent/tools/user-guide'
import { useToolCardOpen } from '@/features/agent/tools/useToolCardOpen'
import { ToolCallCard, ToolCallHeader, type ToolCallStatus } from './_shared/ToolCallHeader'

export interface SearchUserGuideViewProps {
  args: SearchUserGuideArgs
  parsed: SearchUserGuideResult | null
  running: boolean
  errored: boolean
  /** Force the card expanded even with no body. Stories pass true. */
  defaultOpen?: boolean
}

/**
 * Pure presentation for a `search_user_guide` tool-result card: which guide
 * pages mention the exact term, each hit linking to its section. An empty
 * result is shown as such — "no page mentions X" is the honest signal that
 * the guide does not cover the term.
 */
export function SearchUserGuideView({
  args,
  parsed,
  running,
  errored,
  defaultOpen = false,
}: SearchUserGuideViewProps) {
  const { t } = useTranslation()
  const hits = parsed?.hits ?? []
  const count = parsed?.count ?? hits.length
  const failed = errored || parsed?.status === 'unavailable' || parsed?.status === 'error'

  const label = running
    ? t('chat.userGuide.searching')
    : failed
      ? t('chat.userGuide.searchFailed')
      : count === 0
        ? t('chat.userGuide.noHits')
        : t('chat.userGuide.hits', { count })

  const headerStatus: ToolCallStatus = running ? 'running' : failed ? 'error' : 'complete'
  const description = (
    <>
      {label}{' '}
      <code className="ml-0.5 rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-foreground">
        "{args.term}"
      </code>
    </>
  )

  const hasBody = !running && (failed || hits.length > 0)
  const { open, onToggle } = useToolCardOpen(hasBody, defaultOpen, failed)

  return (
    <ToolCallCard>
      <ToolCallHeader
        status={headerStatus}
        icon={BookOpenIcon}
        toolName="search_user_guide"
        description={description}
        expandable={hasBody}
        open={open}
        onToggle={onToggle}
      />

      {open && (
        <div className="flex flex-col gap-2 border-t border-border px-4 py-3 text-xs">
          {parsed?.status === 'unavailable' && (
            <p className="text-muted-foreground">{t('chat.userGuide.unavailable')}</p>
          )}
          {parsed?.status === 'error' && (
            <p className="whitespace-pre-wrap text-muted-foreground">{parsed.error}</p>
          )}
          {hits.length > 0 && (
            <ul className="flex flex-col gap-1.5">
              {hits.map(hit => (
                <li key={`${hit.locale}:${hit.slug}:${hit.anchor ?? ''}:${hit.line}`}>
                  <a
                    href={hit.url}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-primary underline-offset-2 hover:underline max-md:inline-block max-md:py-1.5"
                  >
                    {hitLabel(hit)}
                  </a>
                  {/* Phone: two lines of the matching text; one truncated
                      line shows barely five words at this width. */}
                  <p className="text-muted-foreground max-md:line-clamp-2 md:truncate">
                    {hit.line}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </ToolCallCard>
  )
}
